/**
 * Utilities for working with measurement data in cocktail recipes
 */

/**
 * Parses a measurement string into structured data
 * @param {string} rawAmount - The measurement string to parse
 * @returns {Object} Structured measurement data
 */
export const parseMeasurement = (rawAmount) => {
    if (!rawAmount || typeof rawAmount !== 'string') {
      return { value: null, unit: null, raw: rawAmount || '' };
    }
  
    // Store original value for display purposes
    const raw = rawAmount.trim();
    
    // Common fraction characters
    const fractionMap = {
      '½': 0.5,
      '¼': 0.25,
      '¾': 0.75,
      '⅓': 1/3,
      '⅔': 2/3,
      '⅛': 0.125,
      '⅜': 0.375,
      '⅝': 0.625,
      '⅞': 0.875
    };
  
    // Units we want to recognize (lowercase for comparison)
    const validUnits = [
      'oz', 'ounce', 'ounces',
      'ml', 'milliliter', 'milliliters',
      'cl', 'centiliter', 'centiliters',
      'dash', 'dashes',
      'drop', 'drops',
      'tsp', 'teaspoon', 'teaspoons',
      'tbsp', 'tablespoon', 'tablespoons',
      'barspoon', 'barspoons',
      'pinch', 'pinches',
      'cup', 'cups'
    ];
    
    // Normalize the input: convert unicode fractions and cleanup
    let normalized = raw.toLowerCase();
    
    // Replace unicode fractions with decimal equivalents
    Object.entries(fractionMap).forEach(([fraction, decimal]) => {
      normalized = normalized.replace(fraction, decimal.toString());
    });
  
    // Handle mixed numbers (e.g., "1 1/2 oz")
    normalized = normalized.replace(/(\d+)\s+(\d+)\/(\d+)/g, (_, whole, num, denom) => {
      return (parseInt(whole) + parseInt(num) / parseInt(denom)).toString();
    });
  
    // Handle regular fractions (e.g., "1/2 oz")
    normalized = normalized.replace(/(\d+)\/(\d+)/g, (_, num, denom) => {
      return (parseInt(num) / parseInt(denom)).toString();
    });
  
    // Extract value and unit
    // Pattern looks for numbers (incl. decimals) followed by optional whitespace and then a unit
    const pattern = /^([\d.]+)\s*(.+)$/;
    const match = normalized.match(pattern);
  
    if (!match) {
      // Handle special cases like "a dash" or "some" or just unit names
      const unitOnlyPattern = new RegExp(`^(a |an |some )?(${validUnits.join('|')})s?$`);
      const unitMatch = normalized.match(unitOnlyPattern);
      
      if (unitMatch) {
        return {
          value: 1, // Default to 1 for these cases
          unit: standardizeUnit(unitMatch[2]),
          raw
        };
      }
      
      // If it doesn't match expected patterns, return with null value
      return { value: null, unit: null, raw };
    }
  
    const [, valueStr, unitStr] = match;
    const value = parseFloat(valueStr);
    let unit = unitStr.trim();
  
    // Find the matching unit from our valid units
    const matchedUnit = validUnits.find(validUnit => 
      unit === validUnit || unit.startsWith(validUnit + ' ') || unit.endsWith(' ' + validUnit) || unit.includes(validUnit)
    );
  
    return {
      value: isNaN(value) ? null : value,
      unit: matchedUnit ? standardizeUnit(matchedUnit) : unitStr,
      raw
    };
  };
  
  /**
   * Standardizes unit names to a consistent format
   * @param {string} unit - The unit to standardize
   * @returns {string} The standardized unit name
   */
  const standardizeUnit = (unit) => {
    const unitMap = {
      'ounce': 'oz',
      'ounces': 'oz',
      'milliliter': 'ml',
      'milliliters': 'ml',
      'centiliter': 'cl',
      'centiliters': 'cl',
      'teaspoon': 'tsp',
      'teaspoons': 'tsp',
      'tablespoon': 'tbsp',
      'tablespoons': 'tbsp',
      'dash': 'dash',
      'dashes': 'dash',
      'drop': 'drop',
      'drops': 'drop',
      'barspoon': 'barspoon',
      'barspoons': 'barspoon',
      'pinch': 'pinch',
      'pinches': 'pinch',
      'cup': 'cup',
      'cups': 'cup'
    };
    
    return unitMap[unit] || unit;
  };
  
  /**
   * Formats a measurement object into a user-friendly string
   * @param {Object} measurement - The measurement object
   * @param {boolean} useOriginal - Whether to use the original raw string if available
   * @returns {string} Formatted measurement string
   */
  export const formatMeasurement = (measurement, useOriginal = true) => {
    // If raw value exists and we want to use original format, return it
    if (useOriginal && measurement?.raw) {
      return measurement.raw;
    }
    
    // Handle null or undefined values
    if (!measurement || measurement.value === null || measurement.value === undefined) {
      return measurement?.unit ? measurement.unit : '';
    }
    
    // Format the value
    let formattedValue;
    
    // Try to convert decimals to fractions for common values
    if (measurement.value === 0.25) formattedValue = '¼';
    else if (measurement.value === 0.5) formattedValue = '½';
    else if (measurement.value === 0.75) formattedValue = '¾';
    else if (measurement.value === 0.33 || measurement.value === 1/3) formattedValue = '⅓';
    else if (measurement.value === 0.67 || measurement.value === 2/3) formattedValue = '⅔';
    else formattedValue = measurement.value.toString();
    
    // Return the formatted string
    return `${formattedValue} ${measurement.unit || ''}`.trim();
  };
  
  /**
   * Convert between different units of measurement
   * @param {Object} measurement - The measurement object to convert
   * @param {string} targetUnit - The unit to convert to
   * @returns {Object} The converted measurement
   */
  export const convertMeasurement = (measurement, targetUnit) => {
    if (!measurement?.value || !measurement?.unit) {
      return { ...measurement };
    }
    
    // Conversion table - defines conversion factors to a standard unit (ml)
    const toMl = {
      'oz': 29.5735,
      'ml': 1,
      'cl': 10,
      'tsp': 4.92892,
      'tbsp': 14.7868,
      'cup': 236.588,
      // Approximate conversions for non-exact measurements
      'dash': 0.92, // ~1ml
      'drop': 0.05, // ~1/20 ml
      'barspoon': 5, // ~5ml
      'pinch': 0.36 // Small pinch
    };
    
    // Check if we can convert between these units
    if (!toMl[measurement.unit] || !toMl[targetUnit]) {
      return { ...measurement }; // Return original if conversion not possible
    }
    
    // Convert to target unit
    const valueInMl = measurement.value * toMl[measurement.unit];
    const convertedValue = valueInMl / toMl[targetUnit];
    
    return {
      value: parseFloat(convertedValue.toFixed(4)), // Round to 4 decimal places
      unit: targetUnit,
      raw: measurement.raw
    };
  };
  
  /**
   * Scale a measurement by a factor
   * @param {Object} measurement - The measurement object to scale
   * @param {number} factor - The factor to scale by
   * @returns {Object} The scaled measurement
   */
  export const scaleMeasurement = (measurement, factor) => {
    if (!measurement?.value) {
      return { ...measurement };
    }
    
    return {
      value: parseFloat((measurement.value * factor).toFixed(4)),
      unit: measurement.unit,
      raw: measurement.raw
    };
  };
  
  /**
   * Get display amount for an ingredient based on measurement settings
   * @param {Object} ingredient - The ingredient object
   * @param {Object} options - Display options
   * @returns {string} The formatted amount string
   */
  export const getDisplayAmount = (ingredient, options = {}) => {
    const { 
      useOriginal = true, 
      scaleFactor = 1, 
      targetSystem = null // 'imperial' or 'metric' or null for no conversion
    } = options;
    
    // Fall back to original amount string if no measurement data available
    if (!ingredient.measurement) {
      return ingredient.amount || '';
    }
    
    // Step 1: Scale the measurement if needed
    let result = ingredient.measurement;
    if (scaleFactor !== 1) {
      result = scaleMeasurement(result, scaleFactor);
    }
    
    // Step 2: Convert units if a target system is specified
    if (targetSystem) {
      const currentSystem = getUnitSystem(result.unit);
      
      // Only convert if systems differ
      if (currentSystem !== targetSystem && currentSystem !== 'other') {
        if (targetSystem === 'metric' && currentSystem === 'imperial') {
          result = convertMeasurement(result, 'ml');
        } else if (targetSystem === 'imperial' && currentSystem === 'metric') {
          result = convertMeasurement(result, 'oz');
        }
      }
    }
    
    // Step 3: Format the measurement
    return formatMeasurement(result, useOriginal && scaleFactor === 1);
  };
  
  /**
   * Determine which system a unit belongs to
   * @param {string} unit - The unit
   * @returns {string} 'imperial', 'metric', or 'other'
   */
  export const getUnitSystem = (unit) => {
    if (!unit) return 'other';
    
    const imperialUnits = ['oz', 'tsp', 'tbsp', 'cup'];
    const metricUnits = ['ml', 'cl', 'l'];
    
    if (imperialUnits.includes(unit)) return 'imperial';
    if (metricUnits.includes(unit)) return 'metric';
    return 'other';
  };